export const Sex = {
    Female: 0,
    Male: 1,
    NotSpecified: 2,
} as const;

export type SexValue = (typeof Sex)[keyof typeof Sex];

export const SEX_NAMES: Record<string, SexValue> = {
    Female: Sex.Female,
    Male: Sex.Male,
    NotSpecified: Sex.NotSpecified,
};

export const SEX_LABEL_KEYS: Record<SexValue, string> = {
    [Sex.Female]: 'ContactsPage.sexFemale',
    [Sex.Male]: 'ContactsPage.sexMale',
    [Sex.NotSpecified]: 'common.notSpecified',
};

export const AGE_BRACKETS = [
    { value: '0-17', ageFrom: 0, ageTo: 17 },
    { value: '18-24', ageFrom: 18, ageTo: 24 },
    { value: '25-34', ageFrom: 25, ageTo: 34 },
    { value: '35-44', ageFrom: 35, ageTo: 44 },
    { value: '45-54', ageFrom: 45, ageTo: 54 },
    { value: '55-64', ageFrom: 55, ageTo: 64 },
    { value: '65+', ageFrom: 65, ageTo: null },
] as const;

export const CONTACT_LANGUAGES = [
    'ar',
    'bg',
    'bn',
    'cs',
    'da',
    'de',
    'el',
    'en',
    'es',
    'et',
    'fa',
    'fi',
    'fr',
    'he',
    'hi',
    'hr',
    'hu',
    'id',
    'it',
    'ja',
    'ko',
    'lt',
    'lv',
    'ms',
    'nl',
    'no',
    'pl',
    'pt',
    'ro',
    'ru',
    'sk',
    'sl',
    'sr',
    'sv',
    'sw',
    'th',
    'tr',
    'uk',
    'ur',
    'vi',
    'zh',
];

type BirthDateParts = {
    birthYear?: number | null;
    birthMonth?: number | null;
    birthDay?: number | null;
};

export const MAX_AGE = 150;
export const EARLIEST_BIRTH_YEAR = 1900;
export const EARLIEST_BIRTH_DATE = new Date(EARLIEST_BIRTH_YEAR, 0, 1);

const pad = (value: number) => `${value}`.padStart(2, '0');

export const languageName = (tag: string, uiLanguage?: string) => {
    if (!tag) return '';
    try {
        const names = new Intl.DisplayNames([uiLanguage || 'en'], { type: 'language', languageDisplay: 'standard' });
        return names.of(tag) || tag;
    } catch {
        return tag;
    }
};

export const baseLanguage = (tag?: string | null) =>
    `${tag ?? ''}`.trim().replace(/_/g, '-').split('-')[0].toLowerCase();

export const languageOptions = (uiLanguage?: string, extraTags: string[] = []) => {
    const tags = [...new Set([...CONTACT_LANGUAGES, ...extraTags.filter(Boolean)])];
    const named = new Map<string, { value: string; label: string }>();

    tags.forEach(tag => {
        const label = languageName(tag, uiLanguage);
        if (!named.has(label)) named.set(label, { value: tag, label });
    });

    return [...named.values()].sort((a, b) => a.label.localeCompare(b.label, uiLanguage || 'en'));
};

export const MONTHS_IN_YEAR = 12;

export const monthOptions = (uiLanguage?: string) =>
    Array.from({ length: MONTHS_IN_YEAR }, (_, index) => {
        const month = index + 1;
        return {
            value: month,
            label: new Date(Date.UTC(2024, index, 1)).toLocaleString(uiLanguage || 'en', {
                month: 'long',
                timeZone: 'UTC',
            }),
        };
    });

export const toDateInputValue = ({ birthYear, birthMonth, birthDay }: BirthDateParts) =>
    birthYear && birthMonth && birthDay ? `${birthYear}-${pad(birthMonth)}-${pad(birthDay)}` : '';

export const fromDateInputValue = (value: string): BirthDateParts => {
    const [year, month, day] = (value || '').split('-').map(Number);
    return year && month && day
        ? { birthYear: year, birthMonth: month, birthDay: day }
        : { birthYear: null, birthMonth: null, birthDay: null };
};

export const dateToInputValue = (date: Date | null) =>
    date
        ? toDateInputValue({ birthYear: date.getFullYear(), birthMonth: date.getMonth() + 1, birthDay: date.getDate() })
        : '';

export const dateFromInputValue = (value: string) => {
    const { birthYear, birthMonth, birthDay } = fromDateInputValue(value);
    return birthYear && birthMonth && birthDay ? new Date(birthYear, birthMonth - 1, birthDay) : null;
};

export const hasBirthDateWithoutYear = ({ birthYear, birthMonth }: BirthDateParts) => !birthYear && !!birthMonth;

export const storedBirthDateWithoutYear = (contact: BirthDateParts | null | undefined) =>
    contact && hasBirthDateWithoutYear(contact) ? contact : null;

export const birthDateForSubmit = (value: string, stored: BirthDateParts | null): BirthDateParts =>
    !value && stored
        ? { birthYear: null, birthMonth: stored.birthMonth, birthDay: stored.birthDay }
        : fromDateInputValue(value);

export const formatBirthDate = (parts: BirthDateParts, uiLanguage?: string) => {
    const { birthYear, birthMonth, birthDay } = parts;
    if (!birthMonth || !birthDay) return '';

    const date = new Date(Date.UTC(birthYear || 2024, birthMonth - 1, birthDay));
    return date.toLocaleDateString(uiLanguage || 'en', {
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC',
        ...(birthYear ? { year: 'numeric' } : {}),
    });
};

const BIRTH_DATE_PATTERN = /^(?:\d{4}-)?\d{2}-\d{2}$/;
const UNKNOWN_YEAR_PREFIX = '0000';
export const LEAP_YEAR = 2024;
const LANGUAGE_TAG_PATTERN = /^[A-Za-z]{2,3}(-[A-Za-z0-9]{1,8})*$/;

export const isSexShaped = (value?: string) =>
    !value || Object.keys(SEX_NAMES).some(name => name.toLowerCase() === value.trim().toLowerCase());

export const normalizeBirthDate = (value?: string) => {
    const trimmed = `${value ?? ''}`.trim();
    return trimmed.startsWith(`${UNKNOWN_YEAR_PREFIX}-`) ? trimmed.slice(UNKNOWN_YEAR_PREFIX.length + 1) : trimmed;
};

export const isBirthDateShaped = (value?: string) => {
    if (!value) return true;

    const normalized = normalizeBirthDate(value);
    if (!BIRTH_DATE_PATTERN.test(normalized)) return false;

    const parts = normalized.split('-').map(Number);
    const year = parts.length === 3 ? parts[0] : null;
    const [month, day] = parts.slice(-2);
    const date = new Date(Date.UTC(year ?? LEAP_YEAR, month - 1, day));
    date.setUTCFullYear(year ?? LEAP_YEAR);
    if (date.getUTCMonth() + 1 !== month || date.getUTCDate() !== day) return false;

    return year === null || (year >= EARLIEST_BIRTH_YEAR && date <= new Date());
};

export const isLanguageShaped = (value?: string) =>
    !value || (value.trim().length <= 35 && LANGUAGE_TAG_PATTERN.test(value.trim().replace(/_/g, '-')));

const narrowing = <T>(selected: T[] | undefined, optionCount: number) =>
    !selected || selected.length === optionCount ? [] : selected;

export const toDemographicsQuery = (filter: {
    sexes?: SexValue[];
    languages?: string[];
    birthMonths?: number[];
    ageBracket?: string;
}) => {
    const bracket = AGE_BRACKETS.find(item => item.value === filter.ageBracket);
    return {
        sexes: narrowing(filter.sexes, Object.keys(SEX_LABEL_KEYS).length),
        languages: narrowing(filter.languages, CONTACT_LANGUAGES.length),
        birthMonths: narrowing(filter.birthMonths, MONTHS_IN_YEAR),
        ageFrom: bracket?.ageFrom ?? null,
        ageTo: bracket?.ageTo ?? null,
    };
};
